/* eslint-disable react/no-unused-prop-types */
import React, { useCallback } from 'react';

import Box from '@mui/material/Box';

import Image from 'src/components/ui/Image';
import Video from 'src/components/ui/Video';

import { MediumType } from 'src/types/__generated__/graphql';
import type { Medium } from 'src/types/__generated__/graphql';

import { defaultAudioImagePath, defaultWoopenImagePath } from 'src/constants/defaultImages';
import { styledTag } from 'src/utils/styled';

const Grid = styledTag('div')<{
    $gap: string;
}>`
    display: grid;
    grid-template-columns: 66% 33%;
    grid-template-rows: 50% 50%;
    ${({ $gap }) => `gap: ${$gap};`}
`;

const MediumContainer = styledTag('div')<{
    $gridRow?: string;
}>`
    ${({ $gridRow }) => $gridRow && `grid-row: ${$gridRow};`}
    overflow: hidden;
    display: grid;
    justify-content: center;
    align-items: center;
    position: relative;
`;

const ShowcaseContainer = styledTag('div')<{
    $height: number;
    $borderRadius?: string;
}>`
    display: flex;
    flex-direction: row;
    overflow: hidden;
    ${({ $height }) => `height: ${$height / 16}rem;`}
    ${({ $borderRadius }) => $borderRadius && `border-radius: ${$borderRadius};`}
    width: 100%;
    > div {
        width: 100%;
    }
`;

interface Props {
    showcaseMedia?: null | Array<Pick<Medium, 'url' | 'type'> | null>;
    height: number;
    borderRadius?: string;
    gap?: string;
    minNumberOfMediumToShowGrid?: number;
}

type RenderImageOrVideoParams = {
    medium?: Pick<Medium, 'url' | 'type' | 'isTranscoding'> | null;
    imageStyle?: React.CSSProperties;
    dataTargetId: string;
};

const ShowcaseGrid: React.FC<Props> = ({
    showcaseMedia,
    height,
    borderRadius,
    gap = '0.187rem',
    minNumberOfMediumToShowGrid = 3,
}) => {
    const renderImageOrVideo = useCallback(
        ({ medium, dataTargetId }: RenderImageOrVideoParams) =>
            medium?.type === MediumType.Video ? (
                <Video src={medium.url} isTranscoding={Boolean(medium.isTranscoding)} muted />
            ) : (
                <Image
                    key={
                        medium?.type === MediumType.Audio
                            ? defaultAudioImagePath
                            : medium?.url ?? defaultWoopenImagePath
                    }
                    alt="gallery-main-image"
                    src={
                        medium?.type === MediumType.Audio
                            ? defaultAudioImagePath
                            : medium?.url ?? defaultWoopenImagePath
                    }
                    style={{ objectFit: 'cover' }}
                    data-target-id={dataTargetId}
                    fill
                />
            ),
        [],
    );

    let showcase = (
        <Box position="relative">
            <Image
                src={defaultWoopenImagePath}
                alt="showcase image"
                style={{ objectFit: 'cover' }}
                data-target-id="default-showcase-img"
                fill
            />
        </Box>
    );
    if (showcaseMedia && showcaseMedia.length >= minNumberOfMediumToShowGrid) {
        showcase = (
            <Grid $gap={gap}>
                <MediumContainer $gridRow="span 2">
                    {renderImageOrVideo({
                        medium: showcaseMedia[0],
                        dataTargetId: 'showcase-img-1',
                    })}
                </MediumContainer>
                <MediumContainer>
                    {renderImageOrVideo({
                        medium: showcaseMedia[1],
                        dataTargetId: 'showcase-img-2',
                    })}
                </MediumContainer>
                <MediumContainer>
                    {renderImageOrVideo({
                        medium: showcaseMedia[2],
                        dataTargetId: 'showcase-img-3',
                    })}
                </MediumContainer>
            </Grid>
        );
    } else if (showcaseMedia && showcaseMedia.length > 0) {
        showcase = (
            <MediumContainer>
                {renderImageOrVideo({
                    medium: showcaseMedia[0],
                    dataTargetId: 'main-showcase-img',
                })}
            </MediumContainer>
        );
    }

    return (
        <ShowcaseContainer $height={height} $borderRadius={borderRadius}>
            {showcase}
        </ShowcaseContainer>
    );
};

export default ShowcaseGrid;
